import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import {
  selectCollectionById,
  selectCollectionSummaries,
  selectRecipesByCollection,
  selectHasRecipes,
  selectLastCookedByRecipe,
  selectCookCountByRecipe,
  selectRecipeViewMode,
  selectCollectionSort,
  setRecipeViewMode,
  setCollectionSort,
  deleteCollectionAction,
  reorderCollectionRecipes,
  splitByCooked,
  formatDuration,
  selectRecipeMetaVisibility,
  CUSTOM_ORDER,
  COLLECTION_SORT_OPTIONS,
  useT,
  useAppDispatch,
  useAppSelector,
  type RootState,
} from "@reciperoll/core";
import { ChevronLeft, Trash2, List, Folder } from "react-feather";
import { NotFoundView } from "../components/NotFoundView";
import { RecipeGrid } from "../components/RecipeGrid";
import { RecipeImage } from "../components/RecipeImage";
import { RecipeList } from "../components/RecipeList";
import { SortableList, GripHandle } from "../components/SortableList";
import { RecipeSort } from "../components/RecipeSort";
import { RecipeFilterBar, useRecipeFilter } from "../components/RecipeBarFilter";
import { RecipeViewSwitcher } from "../components/RecipeViewSwitcher";
import { CollectionForm } from "../components/CollectionForm";
import { AddRecipesModal } from "../components/AddRecipesModal";

export default function CollectionDetail() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const cid = id ?? "";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const collection = useAppSelector((s: RootState) => selectCollectionById(s, cid));
  const recipes = useAppSelector((s) => selectRecipesByCollection(s, cid));
  // When the whole library is empty there's nothing to "Add" — offer to create
  // the first recipe instead (matches the app).
  const hasAnyRecipes = useAppSelector(selectHasRecipes);
  const lastCookedAt = useAppSelector(selectLastCookedByRecipe);
  const cookCounts = useAppSelector(selectCookCountByRecipe);
  const viewMode = useAppSelector(selectRecipeViewMode);
  const showMeta = useAppSelector(selectRecipeMetaVisibility);
  // The cover shown on the collection's card (own cover, else a recipe photo) —
  // fed to the edit form's preview so it matches what the user sees.
  const coverImage = useAppSelector(selectCollectionSummaries).find((s) => s.id === cid)
    ?.resolvedImage;

  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  // This collection's own sort preference, persisted per device and per collection.
  const sort = useAppSelector((s: RootState) => selectCollectionSort(s, cid));
  const setSort = (option: string) =>
    dispatch(setCollectionSort({ collectionId: cid, option }));

  // Same filter → sort pipeline as the library (filters reset on unmount).
  const { filters, activeKeys, toggle, clear, filteredRecipes } =
    useRecipeFilter(recipes);
  // Cooked-only sorts (e.g. "Most cooked") split into a cooked group in the sort's
  // order plus a never-cooked remainder shown under a "Never cooked" divider, so
  // recipes in the collection that haven't been cooked aren't hidden. Any other
  // sort (including Custom order) puts everything in `cooked` with no remainder.
  const { cooked, neverCooked } = useMemo(
    () => splitByCooked(filteredRecipes, sort, lastCookedAt, cookCounts),
    [filteredRecipes, sort, lastCookedAt, cookCounts],
  );
  const total = cooked.length + neverCooked.length;
  const showNeverCooked = cooked.length > 0 && neverCooked.length > 0;

  // Drag-to-reorder only in manual "Custom order" + list view, unfiltered. Custom
  // order is never a cooked-only sort, so `cooked` holds the full ordered list.
  const canReorder =
    sort === CUSTOM_ORDER &&
    viewMode === "list" &&
    activeKeys.length === 0 &&
    cooked.length > 0;

  if (!collection) {
    return (
      <NotFoundView
        icon={<Folder className="w-8 h-8 text-terracotta" />}
        title={t("collections.notFoundTitle")}
        message={t("collections.notFoundMessage")}
        actions={
          <Link
            to="/collections"
            className="rounded-xl bg-terracotta hover:bg-terracotta-dark transition-colors px-5 py-2.5 text-sm font-bold text-white"
          >
            {t("collections.backToCollections")}
          </Link>
        }
      />
    );
  }
  const loaded = collection;

  const emptyMessage = recipes.length
    ? t("library.emptyFilters")
    : t("collections.emptyTapAdd");

  const handleDelete = () => {
    if (confirm(t("collections.deleteConfirmWeb", { name: loaded.name }))) {
      dispatch(deleteCollectionAction(loaded.id));
      navigate("/collections");
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <button
        onClick={() => navigate("/collections")}
        className="flex items-center gap-2 text-sm font-semibold text-ink-soft hover:text-ink mb-6 cursor-pointer"
      >
        <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
        {t("nav.collections")}
      </button>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h1 className="font-display text-3xl font-extrabold text-ink truncate">{loaded.name}</h1>
          {loaded.description && <p className="text-ink-soft mt-1">{loaded.description}</p>}
          <p className="text-sm text-ink-faint mt-1">
            {t("library.recipeCount", { count: recipes.length })}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          {hasAnyRecipes ? (
            <button
              onClick={() => setAdding(true)}
              className="rounded-xl bg-terracotta hover:bg-terracotta-dark transition-colors px-4 py-2 text-sm font-bold text-white cursor-pointer"
            >
              {t("collections.addRecipes")}
            </button>
          ) : (
            <button
              onClick={() => navigate("/recipes/new")}
              className="rounded-xl bg-terracotta hover:bg-terracotta-dark transition-colors px-4 py-2 text-sm font-bold text-white cursor-pointer"
            >
              {t("collections.createRecipe")}
            </button>
          )}
          <button
            onClick={() => setEditing(true)}
            className="rounded-xl bg-surface-2 hover:bg-line transition-colors px-4 py-2 text-sm font-semibold text-ink-soft cursor-pointer"
          >
            {t("common.edit")}
          </button>
          <button
            onClick={handleDelete}
            aria-label={t("collections.deleteCollection")}
            className="rounded-xl bg-surface-2 hover:bg-red-50 transition-colors px-3 py-2 text-red-500 cursor-pointer"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {recipes.length > 0 && (
        <>
          <div className="flex items-center justify-between gap-2 mb-4">
            {/* Reordering needs Custom order + list view, unfiltered — offer a
                one-tap way in rather than making users discover that combo. */}
            <div className="min-w-0">
              {canReorder ? (
                <p className="flex items-center gap-1.5 text-sm text-ink-faint">
                  <List className="w-4 h-4 shrink-0" />
                  {t("collections.dragToReorder")}
                </p>
              ) : (
                recipes.length > 1 && (
                  <button
                    onClick={() => {
                      clear();
                      setSort(CUSTOM_ORDER);
                      dispatch(setRecipeViewMode("list"));
                    }}
                    className="flex items-center gap-1.5 text-sm font-semibold text-terracotta-ink hover:text-terracotta-dark cursor-pointer"
                  >
                    <List className="w-4 h-4 shrink-0" />
                    {t("collections.reorderShort")}
                  </button>
                )
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <RecipeSort value={sort} onChange={setSort} options={COLLECTION_SORT_OPTIONS} />
              <RecipeViewSwitcher
                viewMode={viewMode}
                onSwitch={(mode) => dispatch(setRecipeViewMode(mode))}
              />
            </div>
          </div>

          <RecipeFilterBar
            filters={filters}
            activeKeys={activeKeys}
            onToggle={toggle}
            onClear={clear}
            totalCount={recipes.length}
          />
        </>
      )}

      {canReorder ? (
        <SortableList
          items={cooked}
          onReorder={(items) =>
            dispatch(
              reorderCollectionRecipes({
                collectionId: cid,
                recipeIds: items.map((i) => i.id),
              }),
            )
          }
          className="space-y-2"
          renderItem={(recipe, handle) => (
            <div className="flex items-center gap-2 bg-surface border border-line rounded-xl pl-1 pr-3">
              <GripHandle handle={handle} className="w-6 h-16 text-ink-faint" />
              <Link
                to={`/recipe/${recipe.id}`}
                className="flex-1 flex items-center gap-3 min-w-0 py-2 group"
              >
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-surface-2 shrink-0">
                  <RecipeImage recipe={recipe} />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-ink truncate group-hover:text-terracotta-ink">
                    {recipe.title}
                  </p>
                  {showMeta.duration && !!recipe.cookingDuration && (
                    <p className="text-sm text-ink-soft">
                      {formatDuration(recipe.cookingDuration, t)}
                    </p>
                  )}
                </div>
              </Link>
            </div>
          )}
        />
      ) : total === 0 ? (
        viewMode === "grid" ? (
          <RecipeGrid recipes={[]} emptyMessage={emptyMessage} />
        ) : (
          <RecipeList recipes={[]} emptyMessage={emptyMessage} />
        )
      ) : (
        <>
          {viewMode === "grid" ? (
            <RecipeGrid recipes={cooked.length ? cooked : neverCooked} />
          ) : (
            <RecipeList recipes={cooked.length ? cooked : neverCooked} />
          )}

          {showNeverCooked && (
            <>
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-faint mt-10 mb-3">
                {t("library.neverCooked")}
              </h2>
              {viewMode === "grid" ? (
                <RecipeGrid recipes={neverCooked} />
              ) : (
                <RecipeList recipes={neverCooked} />
              )}
            </>
          )}
        </>
      )}

      {editing && (
        <CollectionForm
          collection={loaded}
          previewImage={coverImage}
          onClose={() => setEditing(false)}
        />
      )}
      {adding && <AddRecipesModal collectionId={loaded.id} onClose={() => setAdding(false)} />}
    </div>
  );
}
